import React, { FC, useEffect } from 'react';

import { useUiResourceContext, useConfigContext } from 'common/components/CopyMagic/contexts';
import copyMagicLog from 'common/components/CopyMagic/shared/copyMagicLog';
import { EventDetailType } from './CopyMagicEvents.types';
import CopyMagicEvents from './CopyMagicEvents';

/*
withCopyMagicEventsListener
  HOC wrapper to control the CopyMagic context with browser events.
  Browser events should be listened to and broadcasted from the `CopyMagicEvents` class
  for normalization and parameter enforcement.

  There are two types of events:
  1. Commands manipulate CopyMagic context values
  2. Callbacks broadcast context changes

*/

const withCopyMagicEventsListener = (WrappedComponent: FC) => () => {
  const { generatorName } = useConfigContext();
  const { open, setOpen } = useUiResourceContext();

  useEffect(() => {
    const openCommand = (event: CustomEvent<EventDetailType>) => {
      if (event.detail.generatorName !== generatorName) return;
      setOpen(true);
      copyMagicLog(`Event: ${CopyMagicEvents.EVENT_NAMES.COMMAND_OPEN}`);
    };

    if (open) {
      // Dispatch open callback event
      CopyMagicEvents.dispatch(CopyMagicEvents.EVENT_NAMES.CALLBACK_OPEN, generatorName);
      // Stop listening for open command event
      CopyMagicEvents.removeListener(CopyMagicEvents.EVENT_NAMES.COMMAND_OPEN, openCommand);
    } else {
      // Dispatch close callback event
      CopyMagicEvents.dispatch(CopyMagicEvents.EVENT_NAMES.CALLBACK_CLOSE, generatorName);
      // Listen for open command event
      CopyMagicEvents.addListener(CopyMagicEvents.EVENT_NAMES.COMMAND_OPEN, openCommand);
    }

    return () => {
      CopyMagicEvents.removeListener(CopyMagicEvents.EVENT_NAMES.COMMAND_OPEN, openCommand);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return <WrappedComponent />;
};

export default withCopyMagicEventsListener;
